// Chunked upload for large files (OL-164). Cloudflare, nginx and PHP each cap
// the size of one request, so a big file (a 564 MB demo video, say) goes up in
// pieces. The server checks the whole file once it has arrived, then a queued
// job stores it in B2; this waits for that job before resolving.

// Files above this size go up in pieces. Smaller files keep the single request,
// which stores them straight away.
export const CHUNKED_UPLOAD_THRESHOLD = 20 * 1024 * 1024;

const RETRIES_PER_PIECE = 3;
const POLL_EVERY_MS = 2000;
// A 2 GB file can take a while to reach B2 after the last piece is sent.
const MAX_WAIT_MS = 30 * 60 * 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Send one piece, retrying network errors and server hiccups. A 409 means the
 * server holds a different amount than we think (a lost response, say), so
 * resume from what it actually has.
 *
 * @returns {Promise<number>} bytes the server now holds
 */
const sendPiece = async ({ http, id, offset, piece, sleep }) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      const response = await http.post(`/files/uploads/${id}/chunks`, piece, { params: { offset } });
      return response.data.data.received_bytes;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        const current = await http.get(`/files/uploads/${id}`);
        return current.data.data.received_bytes;
      }
      const retryable = !status || status >= 500;
      if (!retryable || attempt >= RETRIES_PER_PIECE) throw error;
      await sleep(1000 * attempt);
    }
  }
};

/**
 * @param {object}   args
 * @param {File}     args.file
 * @param {Function} [args.onProgress]  ({ sent, total, phase }) — phase is
 *                   'sending' while pieces go up, then 'saving' until stored.
 * @param {object}   [args.http]        axios-like client (injected for tests)
 * @param {Function} [args.sleep]       injected for tests
 * @returns {Promise<{id: string, name: string}>} the stored file
 */
export const uploadInChunks = async ({
  fileableType,
  fileableId,
  parentId = null,
  file,
  onProgress,
  http,
  sleep = wait,
}) => {
  const started = await http.post('/files/uploads', {
    fileable_type: fileableType,
    fileable_uuid: fileableId,
    parent_uuid: parentId,
    name: file.name,
    size: file.size,
  });

  // The response interceptor renames uuid to id.
  const { id, chunk_size: chunkSize } = started.data.data;

  let offset = 0;
  while (offset < file.size) {
    const piece = file.slice(offset, offset + chunkSize);
    offset = await sendPiece({ http, id, offset, piece, sleep });
    onProgress?.({ sent: offset, total: file.size, phase: 'sending' });
  }

  let status = (await http.post(`/files/uploads/${id}/complete`)).data.data;

  onProgress?.({ sent: file.size, total: file.size, phase: 'saving' });

  for (let waited = 0; ; waited += POLL_EVERY_MS) {
    if (status.status === 'done') return status.file;
    if (status.status === 'failed') throw { message: status.error || 'The file could not be saved.' };
    if (waited >= MAX_WAIT_MS) {
      throw { message: 'The file is still being saved. Refresh the page in a few minutes to see it.' };
    }
    await sleep(POLL_EVERY_MS);
    status = (await http.get(`/files/uploads/${id}`)).data.data;
  }
};
